/**
 * Shared page vocabulary for the unified page-aware assistant.
 *
 * ONE entry per page: its id, label, entity params, and the TanStack route
 * ids it covers. The web app derives its routeId→pageId mapping from
 * `routeIds` (so adding a page here wires the frontend automatically), and
 * the API's capability registry (`apps/api/src/assistant/capabilities/registry.ts`)
 * is keyed by `id`. Keeping the vocabulary here — like `user-features.ts` —
 * is what stops the two sides from drifting.
 *
 * Security note: the client-sent pageId only *selects* which capabilities the
 * agent can call; every capability handler re-validates entity ownership
 * through org-scoped repositories.
 */

export const ASSISTANT_PAGES = [
  { id: 'home', label: 'Home dashboard', entityParams: [], routeIds: ['/'] },
  { id: 'flows', label: 'Flows list', entityParams: [], routeIds: ['/flows'] },
  {
    id: 'flow-designer',
    label: 'Flow designer',
    // `runId` arrives via the ?runId search param, not the route path — the
    // web resolver carries it explicitly.
    entityParams: ['workflowId', 'runId'],
    routeIds: ['/flows/$workflowId'],
  },
  {
    id: 'flow-planner',
    label: 'Flow planner',
    entityParams: [],
    routeIds: ['/flows/planner', '/flows/planner/$sessionId'],
  },
  { id: 'runs', label: 'Runs list', entityParams: [], routeIds: ['/runs'] },
  { id: 'run-detail', label: 'Run detail', entityParams: ['runId'], routeIds: ['/runs/$runId'] },
  { id: 'issues', label: 'Issues', entityParams: [], routeIds: ['/issues'] },
  { id: 'suites', label: 'Test suites', entityParams: [], routeIds: ['/suites'] },
  {
    id: 'suite-detail',
    label: 'Suite detail',
    entityParams: ['suiteId'],
    routeIds: ['/suites/$suiteId'],
  },
  { id: 'applications', label: 'Applications', entityParams: [], routeIds: ['/applications'] },
  {
    id: 'application-detail',
    label: 'Application detail',
    entityParams: ['propertyId'],
    routeIds: ['/applications/$propertyId'],
  },
  { id: 'exploratory', label: 'Exploratory tests', entityParams: [], routeIds: ['/exploratory'] },
  { id: 'settings', label: 'Settings', entityParams: [], routeIds: ['/settings'] },
  { id: 'test-data', label: 'Test data', entityParams: [], routeIds: ['/test-data'] },
  { id: 'boards', label: 'Boards', entityParams: [], routeIds: ['/boards'] },
  /** Fallback for routes without a registry entry — global capabilities only. */
  { id: 'unknown', label: 'Unknown page', entityParams: [], routeIds: [] },
] as const;

export type AssistantPageId = (typeof ASSISTANT_PAGES)[number]['id'];

export const ASSISTANT_PAGE_IDS = ASSISTANT_PAGES.map((page) => page.id) as AssistantPageId[];

export function isAssistantPageId(value: string): value is AssistantPageId {
  return (ASSISTANT_PAGE_IDS as string[]).includes(value);
}

export type AssistantPageInfo = (typeof ASSISTANT_PAGES)[number];

export function assistantPageLabel(pageId: AssistantPageId): string {
  return ASSISTANT_PAGES.find((page) => page.id === pageId)?.label ?? pageId;
}

/**
 * The page context the web client sends with every assistant chat request.
 * `params` carries the entity params named in `ASSISTANT_PAGES` for the page
 * (plus nothing else); values are prefixed or raw ids as they appear in the
 * URL — the server extracts UUIDs before touching repositories.
 */
export type AssistantPageContext = {
  pageId: AssistantPageId;
  pathname: string;
  params: Record<string, string>;
};
